// ================================================================================================
// Hatching for the comparison-range bars.
//
// The current period draws solid and every comparison range draws hatched, so the two are
// distinguishable without relying on colour — the row's colour is shared across its bars, and on a
// greyscale print or to a colour-blind reader a lighter fill alone is not a reliable difference.
//
// `itemStyle.decal` needs no registration: the decal visual stage lives in echarts core rather than
// being a pluggable component, and setting `decal` explicitly does not require the `aria` component
// (which only auto-*assigns* decals from a palette, and is not in our tree-shaken build).
// ================================================================================================

/** Opacity of the wash beneath the stripes on a comparison bar. */
export const COMPARISON_FILL_ALPHA = 0.28;

/**
 * Stripe period per comparison range, in the order the ranges appear.
 *
 * One recipe would render ranges 2 and 3 as identical hatches, which is exactly the confusion
 * hatching exists to avoid. Index 0 is the primary range and never hatched, so this is indexed from
 * the first comparison range.
 */
const DASH_ARRAY_Y_BY_RANGE: number[][] = [
  [3, 3],
  [3, 6],
  [1, 3],
];

export type ComparisonDecal = {
  symbol: 'rect';
  color: string;
  backgroundColor: 'none';
  dashArrayX: number[];
  dashArrayY: number[];
  rotation: number;
};

const cache = new Map<string, ComparisonDecal>();

/**
 * The 45° hatch for a comparison bar, matching the dashboard's
 * `repeating-linear-gradient(-45deg, c, c 3px, alpha 3px, alpha 6px)`.
 *
 * `dashArrayY` measures across the stripes before rotation, which is the same axis the CSS 3px/3px
 * is measured on, so the two read identically side by side — worth preserving, because the tooltip
 * swatch uses the CSS version while the bar uses this one.
 *
 * `backgroundColor: 'none'` rather than a solid: the alpha wash belongs on `itemStyle.color` so it
 * composites over whatever is behind the bar (the row-hover band, either theme's paper) instead of
 * punching an opaque hole.
 *
 * Cached by colour and index. ECharts turns each distinct decal object into a canvas pattern tile,
 * and returning a fresh object every render would rebuild every tile on every frame.
 */
export const buildComparisonHatchDecal = (color: string, rangeIndex: number): ComparisonDecal => {
  const dashArrayY = DASH_ARRAY_Y_BY_RANGE[(rangeIndex - 1) % DASH_ARRAY_Y_BY_RANGE.length] ?? DASH_ARRAY_Y_BY_RANGE[0];
  const key = `${color}|${dashArrayY.join(',')}`;

  const cached = cache.get(key);
  if (cached) return cached;

  const decal: ComparisonDecal = {
    symbol: 'rect',
    color,
    backgroundColor: 'none',
    // Continuous along the stripe; the gaps come from dashArrayY.
    dashArrayX: [1, 0],
    dashArrayY,
    rotation: -Math.PI / 4,
  };

  cache.set(key, decal);
  return decal;
};

/**
 * The same hatch as CSS, for the HTML surfaces the canvas cannot reach — the tooltip swatch and the
 * header legend. Kept beside the canvas version so the two cannot drift apart.
 */
export const comparisonHatchCss = (color: string, washColor: string, rangeIndex: number): string => {
  const [on, off] = DASH_ARRAY_Y_BY_RANGE[(rangeIndex - 1) % DASH_ARRAY_Y_BY_RANGE.length] ?? DASH_ARRAY_Y_BY_RANGE[0];
  return `repeating-linear-gradient(-45deg, ${color}, ${color} ${on}px, ${washColor} ${on}px, ${washColor} ${on + off}px)`;
};
